import {Component, HostBinding, Input} from '@angular/core';
import {
  hasGruppeTechnischeSteuerung,
  SymbolControl,
  UIArtefakt,
  UIArtefaktGruppe,
  UiBaseControl,
  UiInputControl,
  uniqueKey
} from '../../models/uiArtefakt';
import {UIDarstellungsart} from '../../models/uiDarstellungsart';
import {EventService} from '../../services/event.service';
import {take} from 'rxjs/operators';
import {ErrorService, KeycloakLv1871Service, RuntimeContext} from '@lv1871/core';
import {UIArtefaktTyp} from '../../models/uiArtefaktTyp';
import {ShellConfigService} from '../../services/shell-config.service';
import {UiArtefaktService} from '../../services/ui-artefakt.service';
import {EnumGruppeTechnischeSteuerung} from '../../models/enumGruppeTechnischeSteuerung';

@Component({
    selector: 'lv-uiartefakt-container',
    templateUrl: './uiartefakt-container.component.html',
    styleUrls: ['./uiartefakt-container.component.scss'],
    standalone: false
})
export class UIArtefaktContainerComponent {
  @HostBinding('class')
  artefaktGruppeClassAttribute: string;

  public artefakteFiltered: UIArtefakt[];
  private _uiArtefakte: UIArtefakt[];

  constructor(private _eventService: EventService, private _errorService: ErrorService,
              private _shellConfigService: ShellConfigService, private _uiArtefaktService: UiArtefaktService,
              private readonly _keycloak: KeycloakLv1871Service) {

  }

  get uiArtefakte(): UIArtefakt[] {
    return this._uiArtefakte;
  }

  @Input()
  set uiArtefakte(uiArtefakte: UIArtefakt[]) {
    this._uiArtefakte = uiArtefakte;
    this._preprocessArtefakte();
  }

  public trackByFn(index: number, item: UIArtefakt) {
    if (item.FeldnameExtern === '/vnKollektiv') {
      return item.WertEingabe;
    }
    return !item.recreateComponent ? uniqueKey(item) : uniqueKey(item) + '-recreate';
  }

  private _preprocessArtefakte() {
    if (!!this._uiArtefakte) {
      this._eventService.uiArtefaktGruppen$.pipe(take(1)).subscribe(alleArtefaktGruppenAsMap => {
        let baseControls = this._uiArtefakte;
        for (let i = 0; i < baseControls.length; i++) {
          if (baseControls[i].UIArtefakttyp === UIArtefaktTyp.BLOCK) {
            if (!!baseControls[i].UIArtefakt) {
              baseControls.splice(i, 1, ...baseControls[i].UIArtefakt);
            } else {
              baseControls.splice(i, 1);
            }
            i--;
          }
        }

        const userAuthentifizierung = this._istUserAuthenticatedAufUmgebungAusserhalbStandalone();

        baseControls = baseControls.filter(artefakt =>
            (artefakt as UiBaseControl).Darstellungsart !== UIDarstellungsart.AUSGEBLENDET &&
            this._filtereArtefakteFuerNichtAuthentifizierteUser(artefakt as UiInputControl, userAuthentifizierung) &&
            (artefakt as SymbolControl).SymbolAlsPraefixJN !== true &&
            (artefakt.UIArtefakttyp !== UIArtefaktTyp.UIVORGANG_REFERENZ || (artefakt as UiInputControl).Quellsystem === 'KDB') &&
            artefakt.UIArtefakttyp !== UIArtefaktTyp.ARTEFAKT_GRUPPE);

        if (this._shellConfigService.getParameter().runtimeContext === RuntimeContext.SB_SHELL) {
          baseControls = baseControls.map(artefakt => {
            if (!artefakt.AntragsabweichungZulaessigJN) {
              return artefakt;
            }
            return ({
              UIArtefakttyp: UIArtefaktTyp.ROETELN,
              UIArtefakt: [artefakt],
              UIArtefaktGruppe: (artefakt as UiBaseControl).UIArtefaktGruppe,
              Identifikator: artefakt.Identifikator + 'RoetelnWrapper'
            } as any as UIArtefakt);
          });
        }

        const gruppenArray = Array.from(alleArtefaktGruppenAsMap.values());
        const artefakteMitNestedArtefaktGruppen: UIArtefakt[] = [];
        let currentGruppenStack: UIArtefaktGruppe[] = [];

        baseControls.forEach((baseControl: UiBaseControl) => {
          if (!!baseControl.UIArtefaktGruppe) {
            const gruppenStack: UIArtefaktGruppe[] = [];
            const foundGruppe = gruppenArray.map(gruppe =>
                this._extractGruppenStack(gruppe, baseControl.UIArtefaktGruppe)).filter(gruppe => !!gruppe)[0];
            if (!foundGruppe) {
              this._errorService.pushWarningMessage('Das Artefakt ' + baseControl.Identifikator +
                  ' ist an eine nicht definierte UIArtefaktGruppe gebunden: ' + baseControl.UIArtefaktGruppe);
              currentGruppenStack = [];
              artefakteMitNestedArtefaktGruppen.push(baseControl);
              return;
            } else {
              for (let nestedGruppe = foundGruppe; !!nestedGruppe; nestedGruppe = nestedGruppe.UIArtefakt[0] as UIArtefaktGruppe) {
                gruppenStack.push(nestedGruppe);
              }
            }

            for (let i = 0; i < gruppenStack.length; i++) {
              if (i === currentGruppenStack.length || currentGruppenStack[i].UIArtefaktGruppe !== gruppenStack[i].UIArtefaktGruppe) {
                currentGruppenStack.splice(i, currentGruppenStack.length - i, ...gruppenStack.slice(i));
                gruppenStack[gruppenStack.length - 1].UIArtefakt = [baseControl];
                if (i === 0) {
                  artefakteMitNestedArtefaktGruppen.push(gruppenStack[i]);
                } else {
                  currentGruppenStack[i - 1].UIArtefakt.push(gruppenStack[i]);
                }
                return;
              }
            }
            currentGruppenStack.splice(gruppenStack.length);
            currentGruppenStack[currentGruppenStack.length - 1].UIArtefakt.push(baseControl);
          } else {
            currentGruppenStack = [];
            artefakteMitNestedArtefaktGruppen.push(baseControl);
          }
        });
        this.artefakteFiltered = artefakteMitNestedArtefaktGruppen;
      });
    }
  }

  private _extractGruppenStack(gruppe: UIArtefaktGruppe, name: string): UIArtefaktGruppe | undefined {
    if (gruppe.UIArtefaktGruppe === name) {
      return {...gruppe, UIArtefakt: []};
    } else if (!!gruppe.UIArtefakt) {
      const nestedGruppen = gruppe.UIArtefakt.map(kindGruppe => this._extractGruppenStack(kindGruppe as UIArtefaktGruppe, name))
      .filter(kindGruppe => !!kindGruppe)[0];
      if (!!nestedGruppen) {
        return {...gruppe, UIArtefakt: [nestedGruppen]};
      }
    }
    return undefined;
  }

  private _filtereArtefakteFuerNichtAuthentifizierteUser(artefakt: UiInputControl, auth: boolean): boolean {
    return auth ? true : !hasGruppeTechnischeSteuerung(artefakt as UiInputControl, EnumGruppeTechnischeSteuerung.AUSBLENDENBEIUSERANONYM);
  }

  private _istUserAuthenticatedAufUmgebungAusserhalbStandalone() {
    return this._shellConfigService.getParameter().runtimeContext !== RuntimeContext.STANDALONE ? this._keycloak.isAuthenticated() : true;
  }

}
